import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api, type Me } from "./api";

// The signed-in person and their companies. A 401 means "not signed in" and resolves to null so
// that the router can send the visitor to the sign-in page.
export function useMe() {
  return useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<Me>("/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/auth/sign-out"),
    onSettled: () => {
      queryClient.clear();
      window.location.assign("/sign-in");
    },
  });
}

export function useSwitchCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => api.post<Me>("/auth/switch-company", { company_id: companyId }),
    onSuccess: () => {
      // Everything cached, and the page itself, belongs to the previous company: start again at
      // Home. (Clearing the cache alone left the open screens showing the previous company.)
      queryClient.clear();
      window.location.assign("/");
    },
  });
}
